import { z } from 'zod';

const IdentifierSchema = z.string()
  .min(3)
  .regex(/^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/, 'Use lowercase reverse-domain style ids, e.g. com.appmage.crm');

const SemverSchema = z.string()
  .regex(/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/, 'Use semantic version format, e.g. 1.0.0');

const JsonSchemaLike = z.object({}).passthrough();

export const ExtensionDependencySchema = z.object({
  extensionId: IdentifierSchema,
  versionRange: z.string().min(1),
  required: z.boolean().optional().default(true),
});

export const ExtensionPublisherSchema = z.object({
  id: IdentifierSchema,
  name: z.string().min(1),
  supportUrl: z.string().url().optional(),
  privacyUrl: z.string().url().optional(),
}).passthrough();

export const ExtensionCompatibilitySchema = z.object({
  framework: z.string().min(1).optional().default('*'),
  apps: z.array(z.string().min(1)).optional().default(['*']),
  requiredServices: z.array(z.string().min(1)).optional().default([]),
}).passthrough();

export const ExtensionTrustSchema = z.object({
  requested: z.enum(['declarative', 'reviewed', 'trusted', 'firstParty']).optional().default('declarative'),
  requiresInProcessCode: z.boolean().optional().default(false),
}).passthrough();

export const ExtensionPermissionsSchema = z.object({
  framework: z.array(z.string().min(1)).optional().default([]),
  contextKeys: z.array(z.string().min(1)).optional().default([]),
  dataScopes: z.array(z.string().min(1)).optional().default([]),
}).passthrough();

export const ExtensionClonePolicySchema = z.object({
  defaultAllowed: z.boolean().optional().default(false),
  allowed: z.array(z.string().min(1)).optional().default([]),
  blocked: z.array(z.string().min(1)).optional().default([]),
}).passthrough();

const CapabilityItemSchema = z.object({
  id: z.string().min(1),
}).passthrough();

export const ExtensionCapabilitiesSchema = z.object({
  skills: z.array(CapabilityItemSchema).optional().default([]),
  playbooks: z.array(CapabilityItemSchema).optional().default([]),
  webApis: z.array(CapabilityItemSchema).optional().default([]),
  workflows: z.array(CapabilityItemSchema).optional().default([]),
  agentTemplates: z.array(CapabilityItemSchema).optional().default([]),
  aiCommands: z.array(CapabilityItemSchema).optional().default([]),
}).passthrough();

export const ExtensionManifestSchema = z.object({
  schemaVersion: z.literal('extension.appbuilder.v1'),
  extensionId: IdentifierSchema,
  version: SemverSchema,
  name: z.string().min(1),
  summary: z.string().min(1),
  description: z.string().optional().default(''),
  publisher: ExtensionPublisherSchema,
  compatibility: ExtensionCompatibilitySchema.optional().default({}),
  trust: ExtensionTrustSchema.optional().default({}),
  dependencies: z.array(ExtensionDependencySchema).optional().default([]),
  permissions: ExtensionPermissionsSchema.optional().default({}),
  installConfigSchema: JsonSchemaLike.optional().default({ type: 'object', properties: {} }),
  capabilities: ExtensionCapabilitiesSchema.optional().default({}),
  clonePolicy: ExtensionClonePolicySchema.optional().default({}),
  billing: JsonSchemaLike.optional().default({ model: 'free' }),
  oauth: z.object({
    providers: z.array(JsonSchemaLike).optional().default([]),
  }).passthrough().optional().default({ providers: [] }),
}).passthrough();

export default ExtensionManifestSchema;
